import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AdminUser } from "@glow-price/shared";

import { adminMe } from "../api/auth";
import {
  getCurrentAdmin,
  getToken,
  setCurrentAdmin,
  subscribeAuth,
} from "./store";

/**
 * `useCurrentAdmin` — single source of truth for "who is logged in" inside
 * the admin UI. Returns the cached safe `AdminUser` row immediately and
 * triggers a background refresh via `/api/admin/auth/me` to keep role /
 * status fresh.
 *
 * Returns `null` when there is no session OR before the first /me response
 * lands and there is no cache yet. Callers (RoleGuard, menus) should treat
 * `null` as "not allowed" — failing closed is the safer default.
 */
export function useCurrentAdmin(): AdminUser | null {
  const [admin, setAdmin] = useState<AdminUser | null>(getCurrentAdmin());
  const [token, setToken] = useState<string | null>(getToken());

  // Keep React state in sync with the auth store. setCurrentAdmin /
  // clearSession both fire `notify()` which trips this listener.
  useEffect(() => subscribeAuth(() => {
    setAdmin(getCurrentAdmin());
    setToken(getToken());
  }), []);

  // Background fetch: only when authenticated. We don't gate the return value
  // on this — the cache already has the answer for most renders.
  const meQuery = useQuery({
    queryKey: ["admin-me"],
    queryFn: adminMe,
    enabled: !!token,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (meQuery.data?.adminUser) {
      setCurrentAdmin(meQuery.data.adminUser);
    }
  }, [meQuery.data]);

  return admin;
}
